import { useCallback, useMemo, useRef, useState } from 'react';
import _get from 'lodash/get';
import { Attachment, FormAnswerMediaFilterInputObject } from 'graphql-common';
import {
  useTaskTimelineFormAnswerMediaByIdsLazyQuery,
  useTaskTimelineFormAnswerMediaIdsQuery,
} from '@lib/graphql-common';

export default function useTaskCompletionsMediaViewer({
  groupKey,
  formAnswerFilters,
}: {
  groupKey: string | null | undefined;
  // Same scope as the grid, so prev/next walks only the media it shows.
  formAnswerFilters: FormAnswerMediaFilterInputObject[];
}) {
  const { data: idsData } = useTaskTimelineFormAnswerMediaIdsQuery({
    variables: { groupKey: groupKey || '', formAnswerFilters },
    skip: !groupKey,
    fetchPolicy: 'network-only',
  });
  const ids = useMemo(
    () =>
      (_get(idsData, 'data.collection', []) as { id: string }[]).map(
        ({ id }) => id,
      ),
    [idsData],
  );

  // A ref (not state) because mutating it must never itself trigger a
  // render — every navigation already ends in `setActiveId`, whose render
  // reads the ref fresh, so the cache stays effectively reactive without
  // needing its own state slot.
  const cacheRef = useRef<Map<string, Attachment>>(new Map());
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isNavLoading, setIsNavLoading] = useState(false);

  const [fetchByIds] = useTaskTimelineFormAnswerMediaByIdsLazyQuery({
    fetchPolicy: 'network-only',
  });

  const cacheItems = useCallback((items: (Attachment | null | undefined)[]) => {
    items.forEach((item) => {
      if (item?.id) cacheRef.current.set(item.id, item);
    });
  }, []);

  const ensureCached = useCallback(
    async (id: string) => {
      if (cacheRef.current.has(id) || !groupKey) return;
      setIsNavLoading(true);
      try {
        const { data } = await fetchByIds({
          variables: { groupKey, ids: [id] },
        });
        cacheItems(_get(data, 'data.collection', []) as Attachment[]);
      } finally {
        setIsNavLoading(false);
      }
    },
    [groupKey, fetchByIds, cacheItems],
  );

  const openAttachment = useCallback(
    (attachment: Attachment) => {
      cacheItems([attachment]);
      setActiveId(attachment.id);
    },
    [cacheItems],
  );

  const close = useCallback(() => setActiveId(null), []);

  const goToOffset = useCallback(
    async (offset: number) => {
      if (!activeId) return;
      const index = ids.indexOf(activeId);
      const targetIndex = index + offset;
      if (index === -1 || targetIndex < 0 || targetIndex >= ids.length) return;
      const targetId = ids[targetIndex];
      await ensureCached(targetId);
      setActiveId(targetId);
    },
    [activeId, ids, ensureCached],
  );

  const goNext = useCallback(() => goToOffset(1), [goToOffset]);
  const goPrev = useCallback(() => goToOffset(-1), [goToOffset]);

  const activeIndex = activeId ? ids.indexOf(activeId) : -1;
  const activeAttachment = activeId ? cacheRef.current.get(activeId) : null;

  return {
    cacheItems,
    openAttachment,
    close,
    goNext,
    goPrev,
    activeAttachment,
    isNavLoading,
    isPrevDisabled: activeIndex <= 0,
    isNextDisabled: activeIndex === -1 || activeIndex >= ids.length - 1,
  };
}
